import React, { useEffect, useMemo, useState } from 'react';
import { Bell, Search, CheckCircle2, AlertTriangle, Info, Trash2, Check, Mail, Settings, Plus, Clock, X, Send, Users, CreditCard, ChevronRight, MoreVertical } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
interface AdminNotificationsCenterProps {
  success: (msg: string) => void;
}
// Generate large mock data
const generateMockNotifications = () => {
  const types = ['info', 'success', 'alert', 'error'];
  const categories = ['system', 'billing', 'users', 'security', 'marketing'];
  const titles = ['Новая жалоба на консультацию', 'Успешная оплата подписки', 'Сбой отправки email', 'Новый астролог ожидает верификации', 'Системное обновление', 'Подозрительная активность', 'Отчет сгенерирован', 'Новый отзыв', 'Превышен лимит API', 'Пользователь заблокирован'];
  const data = [];
  for (let i = 1; i <= 150; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const isRead = Math.random() > 0.3; // 70% read
    data.push({
      id: i,
      title: titles[Math.floor(Math.random() * titles.length)] + ` #${i}`,
      message: `Это детальное описание системного уведомления номер ${i}. Оно содержит важную информацию о произошедшем событии в категории ${category}. Пожалуйста, обратите внимание на детали и примите необходимые меры если требуется.`,
      type,
      category,
      date: i < 5 ? `${i * 10} мин назад` : i < 20 ? `${Math.floor(i / 2)} часов назад` : `${Math.floor(i / 10)} дней назад`,
      read: isRead,
      link: '#',
      metadata: {
        userId: `USR-${1000 + i}`,
        ip: `192.168.1.${i % 255}`,
        browser: 'Chrome 120.0'
      }
    });
  }
  return data;
};
const initialNotifications = generateMockNotifications();
export function AdminNotificationsCenter({
  success
}: AdminNotificationsCenterProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    read: 'all',
    category: 'all'
  });
  const [notifications, setNotifications] = useState(initialNotifications);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);
  const tabs = [{
    id: 'all',
    label: 'Все уведомления',
    icon: <Bell className="w-4 h-4" data-id="element-6422" />
  }, {
    id: 'unread',
    label: 'Непрочитанные',
    icon: <Mail className="w-4 h-4" data-id="element-6423" />
  }, {
    id: 'system',
    label: 'Системные',
    icon: <Settings className="w-4 h-4" data-id="element-6424" />
  }, {
    id: 'users',
    label: 'Пользователи',
    icon: <Users className="w-4 h-4" data-id="element-6425" />
  }, {
    id: 'billing',
    label: 'Финансы',
    icon: <CreditCard className="w-4 h-4" data-id="element-6426" />
  }, {
    id: 'security',
    label: 'Безопасность',
    icon: <AlertTriangle className="w-4 h-4" data-id="element-6427" />
  }];
  // Filtering logic
  const filteredNotifications = useMemo(() => {
    return notifications.filter(n => {
      if (activeTab === 'unread' && n.read) return false;
      if (activeTab !== 'all' && activeTab !== 'unread' && n.category !== activeTab) return false;
      if (filters.type !== 'all' && n.type !== filters.type) return false;
      if (filters.category !== 'all' && n.category !== filters.category) return false;
      if (filters.read !== 'all') {
        const isRead = !!n.read;
        if (filters.read === 'true' && !isRead) return false;
        if (filters.read === 'false' && isRead) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q) || n.id.toString().includes(q);
      }
      return true;
    });
  }, [notifications, activeTab, filters, searchQuery]);
  // Pagination logic
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const paginatedNotifications = filteredNotifications.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [activeTab, filters, searchQuery]);
  // Actions
  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({
      ...n,
      read: true
    })));
    setSelectedIds(new Set());
    success('Все уведомления отмечены как прочитанные');
  };
  const markAsRead = (id: number) => {
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const deleteNotification = (id: number) => {
    setNotifications(notifications.filter(n => n.id !== id));
    const newSelected = new Set(selectedIds);
    newSelected.delete(id);
    setSelectedIds(newSelected);
    success('Уведомление удалено');
  };
  // Bulk Actions
  const toggleSelection = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  const toggleAllOnPage = () => {
    if (selectedIds.size === paginatedNotifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedNotifications.map(n => n.id)));
    }
  };
  const bulkMarkRead = () => {
    setNotifications(notifications.map(n => selectedIds.has(n.id) ? {
      ...n,
      read: true
    } : n));
    setSelectedIds(new Set());
    success(`${selectedIds.size} уведомлений прочитано`);
  };
  const bulkDelete = () => {
    setNotifications(notifications.filter(n => !selectedIds.has(n.id)));
    setSelectedIds(new Set());
    success(`${selectedIds.size} уведомлений удалено`);
  };
  // Helpers
  const getIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-6428" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-rose-500" data-id="element-6429" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-6430" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" data-id="element-6431" />;
    }
  };
  const getBgColor = (type: string, read: boolean) => {
    if (read) return 'bg-white';
    switch (type) {
      case 'alert':
        return 'bg-amber-50/50 border-amber-100';
      case 'error':
        return 'bg-rose-50/50 border-rose-100';
      case 'success':
        return 'bg-emerald-50/50 border-emerald-100';
      default:
        return 'bg-blue-50/50 border-blue-100';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'system':
        return 'bg-slate-100 text-slate-700';
      case 'billing':
        return 'bg-green-100 text-green-700';
      case 'users':
        return 'bg-blue-100 text-blue-700';
      case 'security':
        return 'bg-rose-100 text-rose-700';
      case 'marketing':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div className="space-y-6" data-id="element-6432">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-6433">
        <div data-id="element-6434">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-6435">
            Центр уведомлений
          </h2>
          <p className="text-sm text-slate-500" data-id="element-6436">
            Управление системными оповещениями и рассылками (
            {notifications.length} всего)
          </p>
        </div>
        <div className="flex gap-3" data-id="element-6437">
          <Button variant="outline" icon={<Settings className="w-4 h-4" data-id="element-6439" />} data-id="element-6438">
            Настройки
          </Button>
          <Button variant="primary" icon={<Plus className="w-4 h-4" data-id="element-6441" />} onClick={() => setShowCreateModal(true)} data-id="element-6440">
            Создать рассылку
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-6442">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4" data-id="element-6443">
          <Card padding="sm" className="shadow-none border-slate-200" data-id="element-6444">
            <div className="space-y-1" data-id="element-6445">
              {tabs.map(tab => {
              const count = tab.id === 'all' ? notifications.length : tab.id === 'unread' ? notifications.filter(n => !n.read).length : notifications.filter(n => n.category === tab.id).length;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-6446">
                    <div className="flex items-center gap-2" data-id="element-6447">
                      {tab.icon}
                      <span data-id="element-6448">{tab.label}</span>
                    </div>
                    {count > 0 && <Badge variant={tab.id === 'unread' ? 'pro' : 'neutral'} size="sm" className="text-[10px]" data-id="element-6449">
                        {count > 99 ? '99+' : count}
                      </Badge>}
                  </button>;
            })}
            </div>
          </Card>

          <Card padding="sm" className="shadow-none border-slate-200 bg-slate-50" data-id="element-6450">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-6451">
              Статистика системы
            </h3>
            <div className="space-y-4" data-id="element-6452">
              <div data-id="element-6453">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-6454">
                  <span className="text-slate-500" data-id="element-6455">Доставляемость email</span>
                  <span className="font-bold text-slate-900" data-id="element-6456">98.5%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-6457">
                  <div className="bg-emerald-500 h-2 rounded-full" style={{
                  width: '98.5%'
                }} data-id="element-6458"></div>
                </div>
              </div>
              <div data-id="element-6459">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-6460">
                  <span className="text-slate-500" data-id="element-6461">
                    Открываемость (Open Rate)
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-6462">42.3%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-6463">
                  <div className="bg-indigo-500 h-2 rounded-full" style={{
                  width: '42.3%'
                }} data-id="element-6464"></div>
                </div>
              </div>
              <div data-id="element-6465">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-6466">
                  <span className="text-slate-500" data-id="element-6467">Критичные ошибки (24ч)</span>
                  <span className="font-bold text-rose-600" data-id="element-6468">12</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-6469">
                  <div className="bg-rose-500 h-2 rounded-full" style={{
                  width: '15%'
                }} data-id="element-6470"></div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4" data-id="element-6471">
          <Card padding="sm" className="shadow-none border-slate-200" data-id="element-6472">
            <div className="flex flex-col sm:flex-row justify-between gap-4" data-id="element-6473">
              <div className="relative flex-1 max-w-md" data-id="element-6474">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-6475" />
                <input type="text" placeholder="Поиск по ID, заголовку или тексту..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6476" />
              </div>
              <div className="flex gap-2" data-id="element-6477">
                <Button variant="outline" size="sm" icon={<Check className="w-4 h-4" data-id="element-6479" />} onClick={markAllAsRead} data-id="element-6478">
                  Прочитать все
                </Button>
                <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Search className="w-4 h-4" data-id="element-6481" />} onClick={() => setShowFilters(!showFilters)} data-id="element-6480">
                  Фильтры
                </Button>
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in slide-in-from-top-2" data-id="element-6482">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-6483">
                  <div data-id="element-6484">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6485">
                      Тип важности
                    </label>
                    <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3" value={filters.type} onChange={e => setFilters({
                  ...filters,
                  type: e.target.value
                })} data-id="element-6486">
                      <option value="all" data-id="element-6487">Все типы</option>
                      <option value="info" data-id="element-6488">Инфо</option>
                      <option value="success" data-id="element-6489">Успех</option>
                      <option value="alert" data-id="element-6490">Внимание</option>
                      <option value="error" data-id="element-6491">Ошибка</option>
                    </select>
                  </div>
                  <div data-id="element-6492">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6493">
                      Категория
                    </label>
                    <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3" value={filters.category} onChange={e => setFilters({
                  ...filters,
                  category: e.target.value
                })} data-id="element-6494">
                      <option value="all" data-id="element-6495">Все категории</option>
                      <option value="system" data-id="element-6496">Системные</option>
                      <option value="billing" data-id="element-6497">Финансы</option>
                      <option value="users" data-id="element-6498">Пользователи</option>
                      <option value="security" data-id="element-6499">Безопасность</option>
                    </select>
                  </div>
                  <div data-id="element-6500">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-6501">
                      Статус
                    </label>
                    <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3" value={filters.read} onChange={e => setFilters({
                  ...filters,
                  read: e.target.value
                })} data-id="element-6502">
                      <option value="all" data-id="element-6503">Все статусы</option>
                      <option value="true" data-id="element-6504">Прочитанные</option>
                      <option value="false" data-id="element-6505">Непрочитанные</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-6506">
                  <Button variant="ghost" size="sm" onClick={() => setFilters({
                type: 'all',
                read: 'all',
                category: 'all'
              })} data-id="element-6507">
                    Сбросить фильтры
                  </Button>
                </div>
              </div>}
          </Card>

          {/* Bulk Actions Bar */}
          {selectedIds.size > 0 && <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-center justify-between animate-in fade-in" data-id="element-6508">
              <div className="flex items-center gap-3" data-id="element-6509">
                <span className="text-sm font-bold text-indigo-900" data-id="element-6510">
                  Выбрано: {selectedIds.size}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-6511">
                <Button variant="outline" size="sm" icon={<Check className="w-4 h-4" data-id="element-6513" />} onClick={bulkMarkRead} data-id="element-6512">
                  Прочитать
                </Button>
                <Button variant="outline" size="sm" className="text-rose-600 hover:bg-rose-50 border-rose-200" icon={<Trash2 className="w-4 h-4" data-id="element-6515" />} onClick={bulkDelete} data-id="element-6514">
                  Удалить
                </Button>
              </div>
            </div>}

          {/* Notifications List */}
          <Card padding="none" className="shadow-none border-slate-200 overflow-hidden" data-id="element-6516">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-4" data-id="element-6517">
              <input type="checkbox" className="rounded border-slate-300 w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={paginatedNotifications.length > 0 && selectedIds.size === paginatedNotifications.length} onChange={toggleAllOnPage} title="Выбрать все на странице" data-id="element-6518" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-6519">
                {filteredNotifications.length} уведомлений найдено
              </span>
            </div>

            <div className="divide-y divide-slate-100" data-id="element-6520">
              {paginatedNotifications.length > 0 ? paginatedNotifications.map(notification => <div key={notification.id} className={`p-4 transition-colors flex gap-4 group ${getBgColor(notification.type, notification.read)} hover:bg-slate-50`} data-id="element-6521">
                    <div className="pt-1" data-id="element-6522">
                      <input type="checkbox" className="rounded border-slate-300 w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={selectedIds.has(notification.id)} onChange={() => toggleSelection(notification.id)} data-id="element-6523" />
                    </div>
                    <div className="mt-1 flex-shrink-0" data-id="element-6524">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-6525">
                      <div className="flex justify-between items-start mb-1" data-id="element-6526">
                        <div className="flex items-center gap-2" data-id="element-6527">
                          <h4 className={`text-sm font-bold truncate ${notification.read ? 'text-slate-700' : 'text-slate-900'}`} data-id="element-6528">
                            {notification.title}
                          </h4>
                          {!notification.read && <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" data-id="element-6529"></span>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6530">
                          {!notification.read && <button onClick={() => markAsRead(notification.id)} className="p-1 text-slate-400 hover:text-indigo-600 rounded hover:bg-indigo-50 transition-colors" title="Отметить как прочитанное" data-id="element-6531">
                              <Check className="w-4 h-4" data-id="element-6532" />
                            </button>}
                          <button onClick={() => deleteNotification(notification.id)} className="p-1 text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50 transition-colors" title="Удалить" data-id="element-6533">
                            <Trash2 className="w-4 h-4" data-id="element-6534" />
                          </button>
                        </div>
                      </div>
                      <p className={`text-sm line-clamp-2 mb-2 ${notification.read ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-6535">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-2" data-id="element-6536">
                        <div className="flex items-center gap-3" data-id="element-6537">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${getCategoryColor(notification.category)}`} data-id="element-6538">
                            {notification.category}
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-6539">
                            <Clock className="w-3 h-3" data-id="element-6540" />
                            {notification.date}
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 text-xs px-2 text-indigo-600 hover:bg-indigo-50" onClick={() => setSelectedNotification(notification)} data-id="element-6541">
                          Подробнее <ChevronRight className="w-3 h-3 ml-1" data-id="element-6542" />
                        </Button>
                      </div>
                    </div>
                  </div>) : <div className="text-center py-16 bg-white" data-id="element-6543">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" data-id="element-6544" />
                  <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-6545">
                    Нет уведомлений
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-6546">
                    По вашему запросу ничего не найдено. Попробуйте изменить
                    фильтры.
                  </p>
                </div>}
            </div>

            {/* Pagination inside card footer */}
            {totalPages > 1 && <div className="border-t border-slate-200 p-4 bg-white" data-id="element-6547">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-6548" />
              </div>}
          </Card>
        </div>
      </div>

      {/* === MODALS === */}

      {/* Create Broadcast Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-6549">
          <Card className="w-full max-w-2xl shadow-2xl" data-id="element-6550">
            <div className="flex justify-between items-center mb-6" data-id="element-6551">
              <div data-id="element-6552">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-6553">
                  Создать системную рассылку
                </h3>
                <p className="text-sm text-slate-500" data-id="element-6554">
                  Отправка уведомлений пользователям платформы
                </p>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-6556" />} onClick={() => setShowCreateModal(false)} data-id="element-6555" />
            </div>

            <div className="space-y-4 mb-6" data-id="element-6557">
              <div data-id="element-6558">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6559">
                  Заголовок
                </label>
                <input type="text" placeholder="Важное обновление системы..." className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6560" />
              </div>

              <div data-id="element-6561">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6562">
                  Сообщение
                </label>
                <textarea rows={4} placeholder="Текст уведомления..." className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6563" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-6564">
                <div data-id="element-6565">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6566">
                    Аудитория
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6567">
                    <option data-id="element-6568">Все пользователи (12,450)</option>
                    <option data-id="element-6569">Только Астрологи (342)</option>
                    <option data-id="element-6570">Пользователи с подпиской (1,205)</option>
                    <option data-id="element-6571">Администраторы (8)</option>
                  </select>
                </div>
                <div data-id="element-6572">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6573">
                    Тип уведомления
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6574">
                    <option value="info" data-id="element-6575">Информационное (Синее)</option>
                    <option value="success" data-id="element-6576">Успех (Зеленое)</option>
                    <option value="alert" data-id="element-6577">Внимание (Желтое)</option>
                    <option value="error" data-id="element-6578">Критическое (Красное)</option>
                  </select>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4" data-id="element-6579">
                <div className="flex items-center gap-2 mb-2" data-id="element-6580">
                  <Info className="w-4 h-4 text-slate-400" data-id="element-6581" />
                  <span className="text-sm font-bold text-slate-700" data-id="element-6582">
                    Предпросмотр
                  </span>
                </div>
                <div className="bg-white p-3 rounded border border-slate-200 shadow-sm" data-id="element-6583">
                  <div className="flex gap-3" data-id="element-6584">
                    <div className="mt-0.5" data-id="element-6585">
                      <Info className="w-5 h-5 text-blue-500" data-id="element-6586" />
                    </div>
                    <div data-id="element-6587">
                      <div className="text-sm font-bold text-slate-900" data-id="element-6588">
                        Заголовок уведомления
                      </div>
                      <div className="text-xs text-slate-500 mt-1" data-id="element-6589">
                        Текст уведомления появится здесь...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-6590">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} data-id="element-6591">
                Отмена
              </Button>
              <Button variant="primary" icon={<Send className="w-4 h-4" data-id="element-6593" />} onClick={() => {
            setShowCreateModal(false);
            success('Рассылка успешно запущена');
          }} data-id="element-6592">
                Отправить (12,450)
              </Button>
            </div>
          </Card>
        </div>}

      {/* Notification Detail Modal */}
      {selectedNotification && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-6594">
          <Card className="w-full max-w-2xl shadow-2xl" data-id="element-6595">
            <div className="flex justify-between items-start mb-6" data-id="element-6596">
              <div className="flex gap-3" data-id="element-6597">
                <div className="mt-1 bg-slate-100 p-2 rounded-full" data-id="element-6598">
                  {getIcon(selectedNotification.type)}
                </div>
                <div data-id="element-6599">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6600">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-6601">
                      {selectedNotification.title}
                    </h3>
                    <Badge variant="outline" size="sm" className="text-[10px] uppercase" data-id="element-6602">
                      ID: {selectedNotification.id}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-6603">
                    <span className="flex items-center gap-1" data-id="element-6604">
                      <Clock className="w-3 h-3" data-id="element-6605" /> {selectedNotification.date}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${getCategoryColor(selectedNotification.category)}`} data-id="element-6606">
                      {selectedNotification.category}
                    </span>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-6608" />} onClick={() => setSelectedNotification(null)} data-id="element-6607" />
            </div>

            <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 mb-6" data-id="element-6609">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-id="element-6610">
                {selectedNotification.message}
              </p>
            </div>

            {selectedNotification.metadata && <div className="mb-6" data-id="element-6611">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-6612">
                  Системные метаданные
                </h4>
                <div className="bg-slate-900 rounded-xl p-4 overflow-x-auto" data-id="element-6613">
                  <pre className="text-xs text-emerald-400 font-mono" data-id="element-6614">
                    {JSON.stringify(selectedNotification.metadata, null, 2)}
                  </pre>
                </div>
              </div>}

            <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-6615">
              <Button variant="outline" className="text-rose-600 hover:bg-rose-50 border-rose-200" icon={<Trash2 className="w-4 h-4" data-id="element-6617" />} onClick={() => {
            deleteNotification(selectedNotification.id);
            setSelectedNotification(null);
          }} data-id="element-6616">
                Удалить
              </Button>
              <div className="flex gap-3" data-id="element-6618">
                <Button variant="outline" onClick={() => setSelectedNotification(null)} data-id="element-6619">
                  Закрыть
                </Button>
                {!selectedNotification.read && <Button variant="primary" icon={<Check className="w-4 h-4" data-id="element-6621" />} onClick={() => {
              markAsRead(selectedNotification.id);
              setSelectedNotification(null);
              success('Отмечено как прочитанное');
            }} data-id="element-6620">
                    Прочитано
                  </Button>}
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}